import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { frontendSkills, backendSkills, systemSkills } from "@/mocks/devData";

gsap.registerPlugin(ScrollTrigger);

interface SkillItem { name: string; icon: string; }

function SkillCard({ skill, delay }: { skill: SkillItem; delay: number }) {
  const cardRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const rotX = ((y - cy) / cy) * -12;
    const rotY = ((x - cx) / cx) * 12;
    gsap.to(el, { rotateX: rotX, rotateY: rotY, duration: 0.2, ease: "power2.out", transformPerspective: 600 });
  };

  const handleMouseLeave = () => {
    const el = cardRef.current;
    if (!el) return;
    gsap.to(el, { rotateX: 0, rotateY: 0, duration: 0.5, ease: "elastic.out(1, 0.5)" });
  };

  return (
    <div
      ref={cardRef}
      className="group relative rounded-xl aspect-square w-full flex flex-col items-center justify-center gap-2.5 p-3 transition-colors duration-200 cursor-default"
      style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.09)",
        transformStyle: "preserve-3d",
        animationDelay: `${delay}ms`,
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className="relative w-10 h-10 flex-shrink-0 flex items-center justify-center group-hover:scale-110 transition-transform duration-200"
        style={{ transform: "translateZ(10px)" }}
      >
        <img src={skill.icon} alt={skill.name} className="w-full h-full object-contain" />
      </div>
      <span
        className="text-gray-300 text-xs font-mono text-center leading-tight line-clamp-2 group-hover:text-white transition-colors duration-200"
        style={{ transform: "translateZ(5px)" }}
      >
        {skill.name}
      </span>
      {/* Hover border glow */}
      <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" style={{ border: "1px solid rgba(34,197,94,0.35)" }} />
    </div>
  );
}

function SkillSection({ title, skills, index = 0, isLast = false }: { title: string; skills: SkillItem[]; index?: number; isLast?: boolean }) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(titleRef.current, { opacity: 0, x: -40, rotateY: -15, transformPerspective: 700 }, {
        opacity: 1, x: 0, rotateY: 0, duration: 0.7, ease: "power3.out",
        scrollTrigger: { trigger: titleRef.current, start: "top 88%", once: true },
      });
      if (gridRef.current) {
        const cards = gridRef.current.querySelectorAll(".skill-card-wrap");
        gsap.fromTo(cards, { opacity: 0, y: 35, rotateX: -20, scale: 0.9, transformPerspective: 700 }, {
          opacity: 1, y: 0, rotateX: 0, scale: 1, duration: 0.55, stagger: 0.05, ease: "back.out(1.5)",
          scrollTrigger: { trigger: gridRef.current, start: "top 88%", once: true },
        });
      }
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <div ref={sectionRef} className={isLast ? "" : "mb-10 pb-10"} style={!isLast ? { borderBottom: "1px solid rgba(255,255,255,0.06)" } : undefined}>
      <h3
        ref={titleRef}
        className="text-xs font-mono text-gray-400 mb-5 flex items-center gap-3 uppercase tracking-widest"
      >
        <span className="w-6 h-px" style={{ background: "linear-gradient(to right, rgba(34,197,94,0.7), transparent)" }} />
        {title}
        <span className="flex-1 h-px" style={{ background: "linear-gradient(to right, rgba(255,255,255,0.08), transparent)" }} />
      </h3>
      <div ref={gridRef} className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3" style={{ perspective: "800px" }}>
        {skills.map((s, i) => (
          <div key={s.name} className="skill-card-wrap">
            <SkillCard skill={s} delay={i * 40} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DevSkills() {
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(headerRef.current, { opacity: 0, y: 60, rotateX: -20, transformPerspective: 1000 }, {
        opacity: 1, y: 0, rotateX: 0, duration: 1, ease: "power3.out",
        scrollTrigger: { trigger: headerRef.current, start: "top 85%", once: true },
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      id="skills"
      ref={sectionRef}
      className="relative py-20 md:py-28 overflow-hidden"
      style={{
        background: `
          radial-gradient(at 20% 20%, rgba(22,163,74,0.05) 0px, transparent 50%),
          radial-gradient(at 80% 80%, rgba(22,163,74,0.04) 0px, transparent 50%),
          #000000
        `,
        perspective: "1200px",
      }}
    >
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[350px] rounded-full pointer-events-none" style={{ background: "radial-gradient(ellipse, rgba(34,197,94,0.05) 0%, transparent 70%)", filter: "blur(50px)" }} />

      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8">
        <div ref={headerRef} className="mb-14" style={{ transformStyle: "preserve-3d" }}>
          <p className="text-xs font-mono text-gray-400 tracking-widest uppercase mb-3">My Arsenal</p>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <h2 className="text-4xl md:text-5xl font-light text-white tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Technologies &amp;{" "}<span className="font-black text-green-600">Tools</span>
            </h2>
            <p className="text-gray-400 text-sm max-w-xs leading-relaxed">Technologies I use to bring ideas to life across the full stack.</p>
          </div>
          <div className="mt-6 h-px" style={{ background: "rgba(255,255,255,0.06)" }} />
        </div>

        <div
          className="rounded-3xl p-6 md:p-10"
          style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.08)" }}
        >
          <SkillSection title="Frontend Development" skills={frontendSkills} index={0} />
          <SkillSection title="Backend Development" skills={backendSkills} index={1} />
          <SkillSection title="System & Tools" skills={systemSkills} index={2} isLast />
        </div>
      </div>
    </section>
  );
}